import React, { Fragment, useState, useEffect } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import api from "../../../api";
import FilePreview from "../Common/FilePreview";
import Select from "react-select";


const TCReportEdit = () => {
 const { id } = useParams();
 const navigate = useNavigate();
const [searchParams] = useSearchParams();

const workOrderFromUrl = searchParams.get("workOrder") || "";
const projectFromUrl = searchParams.get("project") || "";
 const [workOrders, setWorkOrders] = useState([]);
 const [boqItems, setBoqItems] = useState([]);
 const [loading, setLoading] = useState(false);

 const [form, setForm] = useState({
 tcNo: "",
 workOrder: "",
 testDate: "",
 clientComments: "",
 });

 // ========================================================
 // ITEMS
 // ========================================================
 const [items, setItems] = useState([]);

const addItem = () => {
 setItems([
 ...items,
 {
 equipment: "",
 testPerformed: "",
 testResults: "",
 status: "Pending",
 description: "",
 unit: "",
 testedQty: 1, // NEW
 },
 ]);
};


 const removeItem = (index) => {
 setItems(items.filter((_, i) => i !== index));
 };

 const handleItemChange = (index, field, value) => {
 const updated = [...items];
 updated[index][field] = value;

 // Equipment selected → auto-fill
 if (field === "equipment") {
 const b = boqItems.find((x) => x._id === value);

 updated[index].boqItemId = b?._id || "";
 updated[index].testPerformed = b?.testType || "";
 updated[index].description = b ? `[WLI-${b.wlino || "-"}] ${b.itemName || b.description}` : "";
 updated[index].unit = b?.unit || "";
 }

 // TESTED QTY VALIDATION
 if (field === "testedQty") {
 const boqId = updated[index].equipment;
 const boqItem = boqItems.find((b) => b._id === boqId);

 const boqQty = Number(boqItem?.quantity || 0);
 const enteredQty = Number(value || 0);

 if (enteredQty > boqQty) {
 swal(
 "Quantity Exceeded",
 `Cannot exceed BOQ quantity (${boqQty})`,
 "warning"
 );

 updated[index].testedQty = boqQty;
 }
 }

 setItems(updated);
};


 const isUsed = (id, currentIndex) =>
 items.some((it, idx) => idx !== currentIndex && it.equipment === id);

 // ========================================================
 // LOAD WORK ORDERS
 // ========================================================
 useEffect(() => {
 api.get("/work-orders").then((res) => {
 if (res.data.success) setWorkOrders(res.data.data);
 });
 }, []);

 // ========================================================
 // FETCH BOQ FROM WORK ORDER
 // ========================================================
 const fetchBOQ = async (workOrderId) => {
 try {
 const res = await api.get(`/work-orders/${workOrderId}/boq`);
 setBoqItems(res.data.success ? res.data.data : []);
 } catch {
 setBoqItems([]);
 }
 };

 // ========================================================
 // LOAD EXISTING TC
 // ========================================================
 useEffect(() => {
 async function loadTC() {
 try {
 const res = await api.get(`/testing-commissioning/${id}`);
 if (!res.data.success) {
 return swal("Error", "Failed to load TC", "error");
 }

 const d = res.data.data;

 setForm({
 tcNo: d.tcNo || d.tcNumber || "",
 workOrder: d.workOrder?._id || "",
 testDate: d.testDate?.split("T")[0] || "",
 clientComments: d.clientComments || "",
 testReport: d.testReport || null,
 });

 setItems(
 d.items.map((it) => ({
 equipment: it.equipment,
 testPerformed: it.testPerformed,
 testResults: it.testResults,
 status: it.status || "Pending",
 description: it.description || "",
 unit: it.unit || "",
 testedQty: it.testedQty || 1, // LOAD SAVED QTY
 }))
);


 if (d.workOrder?._id) fetchBOQ(d.workOrder._id);
 } catch (err) {
 swal("Error", err.message, "error");
 }
 }

 loadTC();
 }, [id]);

 // ========================================================
 // FORM CHANGE
 // ========================================================
 const handleChange = (e) => {
 const { name, value } = e.target;

 setForm((p) => ({ ...p, [name]: value }));

 if (name === "workOrder") {
 fetchBOQ(value);
 setItems([
 {
 equipment: "",
 testPerformed: "",
 testResults: "",
 status: "Pending",
 description: "",
 unit: "",
 testedQty: 1, // NEW
 },
]);

 }
 };
useEffect(() => {
 if (workOrderFromUrl && workOrders.length > 0) {
 setForm((prev) => ({
 ...prev,
 workOrder: workOrderFromUrl,
 }));

 fetchBOQ(workOrderFromUrl);
 }
}, [workOrders, workOrderFromUrl]);
 const handleFile = (e) => {
 setForm((prev) => ({ ...prev, testReport: e.target.files[0] }));
 };

 // ========================================================
 // SUBMIT UPDATE
 // ========================================================
 const handleSubmit = async (e) => {
 e.preventDefault();

 for (const it of items) {
 if (!it.equipment || !it.testPerformed || !it.testResults || !it.testedQty) {
 swal("Error", "All equipment rows must be complete", "error");
 return;
 }
 }

 const fd = new FormData();
 fd.append("workOrder", form.workOrder);
 fd.append("testDate", form.testDate);
 fd.append("clientComments", form.clientComments);
 fd.append("items", JSON.stringify(items));

 if (form.testReport instanceof File) {
 fd.append("testReport", form.testReport);
 }

 setLoading(true);

 try {
 const res = await api.patch(`/testing-commissioning/${id}`, fd);
 if (res.data.success) {
 swal("Updated", "TC Report updated successfully!", "success").then(() => {
 const filterQuery = [
 projectFromUrl ? `project=${projectFromUrl}` : "",
 workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
 ].filter(Boolean).join("&");

 navigate(filterQuery ? `/tc-report?${filterQuery}` : "/tc-report");
 });
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 swal("Error", err.message, "error");
 }

 setLoading(false);
 };

 const required = <span style={{ color: "red" }}>*</span>;

 // ========================================================
 return (
 <Fragment>
 <PageTitle activeMenu="Edit TC Report" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Edit TC Report</Card.Title>
 <button
 className="btn btn-secondary"
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/tc-report?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/tc-report"
 )
}
 >
 Back
 </button>
 </Card.Header>

 <Card.Body>
 <form onSubmit={handleSubmit}>

 {/* TC NO */}
 <div className="mb-3">
 <label>TC No {required}</label>
 <input className="form-control" disabled value={form.tcNo} />
 </div>

 {/* WORK ORDER */}
 <div className="mb-3">
 <label>Work Order {required}</label>
 <select
 name="workOrder"
 className="form-select"
 value={form.workOrder}
 onChange={handleChange}
 >
 <option value="">Select Work Order</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} — {wo.project?.projectName}
 </option>
 ))}
 </select>
 </div>

 {/* EQUIPMENT LIST */}
 <div className="mb-3">
 <h5>Equipment List</h5>
 <button
 type="button"
 className="btn btn-success btn-sm"
 onClick={addItem}
 >
 + Add Equipment
 </button>
 </div>

 {items.map((item, idx) => (
 <div key={idx} className="border p-3 rounded mb-3">
 <div className="d-flex justify-content-between">
 <strong>Equipment #{idx + 1}</strong>
 {idx > 0 && (
 <button
 type="button"
 className="btn btn-danger btn-sm"
 onClick={() => removeItem(idx)}
 >
 Remove
 </button>
 )}
 </div>

 <div className="mt-2">
 <label>Equipment {required}</label>
 <Select
 placeholder="Search & select Equipment..."
 isClearable
 options={boqItems.map((b) => ({
 value: b._id,
 label: `[WLI-${b.wlino || "-"}] ${b.itemName} — ${b.quantity} ${b.unit || ""}`,
 isDisabled: isUsed(b._id, idx),
 }))}
 value={
 boqItems
 .filter((b) => b._id === item.equipment)
 .map((b) => ({
 value: b._id,
 label: `[WLI-${b.wlino || "-"}] ${b.itemName} — ${b.quantity} ${b.unit || ""}`,
 }))[0] || null
 }
 onChange={(selected) => {
 if (selected) {
 handleItemChange(idx, "equipment", selected.value);
 } else {
 handleItemChange(idx, "equipment", "");
 }
 }}
 styles={{
 control: (base) => ({
 ...base,
 minHeight: "38px",
 }),
 }}
/>

 </div>

 <div className="mt-2">
 <label>Description</label>
 <input className="form-control" value={item.description} readOnly />
 </div>

 <div className="mt-2">
 <label>Unit</label>
 <input className="form-control" value={item.unit} readOnly />
 </div>
<div className="mt-2">
 <label>No. of Items Tested *</label>
 <input
 type="number"
 min="1"
 className="form-control"
 value={item.testedQty}
 onChange={(e) =>
 handleItemChange(idx, "testedQty", e.target.value)
 }
 />
</div>
{item.equipment && (
 <small className="text-muted">
 BOQ Quantity:{" "}
 {
 boqItems.find((b) => b._id === item.equipment)?.quantity
 }
 </small>
)}

 <div className="mt-2">
 <label>Test Performed</label>
 <input
 className="form-control"
 value={item.testPerformed}
 onChange={(e) =>
 handleItemChange(idx, "testPerformed", e.target.value)
 }
 />
 </div>

 <div className="mt-2">
 <label>Test Results</label>
 <select
 className="form-select"
 value={item.testResults}
 onChange={(e) =>
 handleItemChange(idx, "testResults", e.target.value)
 }
 >
 <option value="">Select</option>
 <option value="Pass">Pass</option>
 <option value="Fail">Fail</option>
 <option value="Conditional Pass">Conditional Pass</option>
 </select>
 </div>

 <div className="mt-2">
 <label>Status</label>
 <select
 className="form-select"
 value={item.status}
 onChange={(e) =>
 handleItemChange(idx, "status", e.target.value)
 }
 >
 <option value="Pending">Pending</option>
 <option value="NC Raised">NC Raised</option>
 <option value="Retest Required">Retest Required</option>
 </select>
 </div>
 </div>
 ))}

 <div className="mb-3">
 <label>Test Date {required}</label>
 <input
 type="date"
 name="testDate"
 className="form-control"
 value={form.testDate}
 onChange={handleChange}
 />
 </div>

 {/* FILE UPLOAD */}
 <div className="mb-3">
 <label>Attach Test Report</label>
 <input
 type="file"
 className="form-control"
 onChange={handleFile}
 />
 <FilePreview 
 file={form.testReport} 
 onRemove={() => setForm(prev => ({ ...prev, testReport: null }))} 
 />
 </div>

 <div className="mb-3">
 <label>Client Comments</label>
 <textarea
 className="form-control"
 rows={3}
 name="clientComments"
 value={form.clientComments}
 onChange={handleChange}
 />
 </div>

 <button className="btn btn-primary me-2" disabled={loading}>
 {loading ? "Updating..." : "Update"}
 </button>

 <button
 type="button"
 className="btn btn-danger"
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/tc-report?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/tc-report"
 )
}
 >
 Cancel
 </button>

 </form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default TCReportEdit;
